import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const getHeaders = () => {
  const token = localStorage.getItem('fleetflow_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Dashboard Stats
export const getDashboardStats = async () => {
  const response = await axios.get(`${API}/dashboard/stats`, { headers: getHeaders() });
  return response.data;
};

// Missions
export const getMissions = async (status) => {
  const params = status ? { status } : {};
  const response = await axios.get(`${API}/missions`, { headers: getHeaders(), params });
  return response.data;
};

export const getOpportunities = async () => {
  const response = await axios.get(`${API}/missions/opportunities`, { headers: getHeaders() });
  return response.data;
};

export const getMission = async (id) => {
  const response = await axios.get(`${API}/missions/${id}`, { headers: getHeaders() });
  return response.data;
};

export const createMission = async (data) => {
  const response = await axios.post(`${API}/missions`, data, { headers: getHeaders() });
  return response.data;
};

export const updateMissionStatus = async (id, status) => {
  const response = await axios.put(`${API}/missions/${id}/status`, null, { 
    headers: getHeaders(), 
    params: { status }
  });
  return response.data;
};

// Vehicles
export const getVehicles = async () => {
  const response = await axios.get(`${API}/vehicles`, { headers: getHeaders() });
  return response.data;
};

export const getVehicle = async (id) => {
  const response = await axios.get(`${API}/vehicles/${id}`, { headers: getHeaders() });
  return response.data;
};

export const createVehicle = async (data) => {
  const response = await axios.post(`${API}/vehicles`, data, { headers: getHeaders() });
  return response.data;
};

export const updateVehicle = async (id, data) => {
  const response = await axios.put(`${API}/vehicles/${id}`, data, { headers: getHeaders() });
  return response.data;
};

export const deleteVehicle = async (id) => {
  const response = await axios.delete(`${API}/vehicles/${id}`, { headers: getHeaders() });
  return response.data;
};

export const updateVehicleLocation = async (id, lat, lng) => {
  const response = await axios.put(`${API}/vehicles/${id}/location`, null, { 
    headers: getHeaders(),
    params: { lat, lng }
  });
  return response.data;
};

// SmartMatch
export const smartMatch = async (filters) => {
  const response = await axios.post(`${API}/smartmatch`, filters, { headers: getHeaders() });
  return response.data;
};

// Bids
export const createBid = async (data) => {
  const response = await axios.post(`${API}/bids`, data, { headers: getHeaders() });
  return response.data;
};

export const getMissionBids = async (missionId) => {
  const response = await axios.get(`${API}/bids/mission/${missionId}`, { headers: getHeaders() });
  return response.data;
};

export const getMyBids = async () => {
  const response = await axios.get(`${API}/bids/my`, { headers: getHeaders() });
  return response.data;
};

export const acceptBid = async (bidId) => {
  const response = await axios.post(`${API}/bids/${bidId}/accept`, null, { headers: getHeaders() });
  return response.data;
};

export const rejectBid = async (bidId) => {
  const response = await axios.post(`${API}/bids/${bidId}/reject`, null, { headers: getHeaders() });
  return response.data;
};

// Messages
export const sendMessage = async (data) => {
  const response = await axios.post(`${API}/messages`, data, { headers: getHeaders() });
  return response.data;
};

export const getMessages = async () => {
  const response = await axios.get(`${API}/messages`, { headers: getHeaders() });
  return response.data;
};

export const markMessageRead = async (id) => {
  const response = await axios.put(`${API}/messages/${id}/read`, null, { headers: getHeaders() });
  return response.data;
};

// Ratings
export const createRating = async (data) => {
  const response = await axios.post(`${API}/ratings`, data, { headers: getHeaders() });
  return response.data;
};

export const getUserRatings = async (userId) => {
  const response = await axios.get(`${API}/ratings/user/${userId}`, { headers: getHeaders() });
  return response.data;
};

export const getMissionRatings = async (missionId) => {
  const response = await axios.get(`${API}/ratings/mission/${missionId}`, { headers: getHeaders() });
  return response.data;
};

// Payments
export const createCheckoutSession = async (missionId) => {
  const originUrl = window.location.origin;
  const response = await axios.post(`${API}/payments/checkout`, { 
    mission_id: missionId, 
    origin_url: originUrl 
  }, { headers: getHeaders() });
  return response.data;
};

export const getPaymentStatus = async (sessionId) => {
  const response = await axios.get(`${API}/payments/status/${sessionId}`, { headers: getHeaders() });
  return response.data;
};

export const getPaymentHistory = async () => {
  const response = await axios.get(`${API}/payments/history`, { headers: getHeaders() });
  return response.data;
};

// Pricing
export const getPricing = async () => {
  const response = await axios.get(`${API}/pricing`);
  return response.data;
};

// Compliance
export const getCompliance = async () => {
  const response = await axios.get(`${API}/compliance`);
  return response.data;
};

// KYC
export const getKYCProfile = async () => {
  const response = await axios.get(`${API}/kyc/profile`, { headers: getHeaders() });
  return response.data;
};

export const getKYCDocuments = async () => {
  const response = await axios.get(`${API}/kyc/documents`, { headers: getHeaders() });
  return response.data;
};

export const getRequiredKYCDocuments = async () => {
  const response = await axios.get(`${API}/kyc/required-documents`, { headers: getHeaders() });
  return response.data;
};

export const uploadKYCDocument = async (documentType, documentBase64, documentName, expiryDate = null) => {
  const response = await axios.post(`${API}/kyc/documents`, {
    document_type: documentType,
    document_base64: documentBase64,
    document_name: documentName,
    expiry_date: expiryDate
  }, { headers: getHeaders() });
  return response.data;
};

export const verifySIRET = async (siret) => {
  const response = await axios.post(`${API}/kyc/verify-siret`, { siret }, { headers: getHeaders() });
  return response.data;
};

export const getComplianceStatus = async () => {
  const response = await axios.get(`${API}/compliance/status`, { headers: getHeaders() });
  return response.data;
};

export const getComplianceChecks = async () => {
  const response = await axios.get(`${API}/compliance/checks`, { headers: getHeaders() });
  return response.data;
};

